'use strict'

// Cross-file FUNCTION import — the sanctioned pattern (see
// personaSession.js's own header; same as sdkClient.js's imports). Never a
// bare module-local VALUE read from inside an exported function (FA204).
import { clearPersonaSession, writePersonaSession } from './personaSession.js'

// Persona (tickets/opus.md PERSONA-1/2/3, tickets/sonnet.md PERSONA-4/5,
// tickets/fable.md PERSONA-6) — shared plumbing for the "view as <role>"
// affordance. Every function here is safe to land AHEAD of the server
// contract: each one degrades to an inert no-op when the state/SDK surface
// it reads doesn't exist yet. Status 2026-08-12: PERSONA-1 (server
// 886a9b27, claimsToScope resolution) and PERSONA-4 (sdk 26402c58,
// sdk.startPersona/endPersona/getPersona) HAVE landed; `_personaSession`
// shell state is PERSONA-5's still-open half. See the file-level comment
// in components/PersonaPill.js for the full premise-check and the design
// decisions this file implements.

// ── Role catalog — verified against the LIVE server model, not the ticket's
// example list ──────────────────────────────────────────────────────────
// The HARD INVARIANT in tickets/fable.md PERSONA-6 (repeated verbatim on
// every PERSONA-1..6 entry) names "admin / manager / member / guest" as an
// illustrative example. Checked against
// server/src/core/constants/memberRoles.js (2026-08-12): 'manager' does not
// exist in ANY real role vocabulary in this repo (ORG_ROLES, WORKSPACE_ROLES,
// or PROJECT_ROLES). 'guest' is real, but only in WORKSPACE_ROLES /
// PROJECT_ROLES — not ORG_ROLES, which is the vocabulary `OrgMember.role`
// (what `claimsToScope`, PERSONA-1's own insertion point, actually reads)
// uses. `server/src/core/constants/permissions.js` already ships a 3-tier
// SIMPLE_ORG_ROLES (['owner', 'admin', 'member']) + a `simpleOrgRole()`
// mapper explicitly documented as being for exactly this use ("claim-
// synthesis... time" — i.e. PERSONA-1's own job).
//
// RESOLVED 2026-08-12 (PERSONA-3): the role-scope question above was decided
// by the shipped stack — the wire vocabulary is the WORKSPACE tier
// (PERSONA_TARGET_ROLES: admin/editor/viewer/guest, owner excluded) on
// server 886a9b27 + sdk 26402c5. Catalog `role` values are therefore WIRE
// values — `PersonaPicker` passes them straight into
// `sdk.startPersona({ role })`, which REFUSES anything outside the enum, so
// a non-enum row here (the earlier tentative 'member') made its picker row
// throw on click. Labels stay product copy: Nika's "[team member]" maps onto
// the `viewer` tier (PERSONA-1's recorded reading: member→viewer,
// manager→editor). 'owner' is deliberately not a row — the HARD INVARIANT
// frames it as the baseline an owner already has, not something to switch
// INTO. Whether to ALSO surface an `editor` ("Manager") row is a product
// call — one line here when wanted; the rank/label tables below already
// cover the full enum.
// `label` carries a bundle KEY — PersonaPill resolves it through polyglot at
// its own render site (docs/I18N-CONVENTION.md, key-in-state).
export const PERSONA_ROLE_CATALOG = [
  { role: 'admin', label: 'members.persona.role.admin' },
  // "sign it as [team member]" (Nika, 2026-08-11, RESIDUALS ANSWERED) —
  // never a tenant's own job title (e.g. bellforge's "Searcher").
  { role: 'viewer', label: 'members.persona.role.teamMember' },
  { role: 'guest', label: 'members.persona.role.guest' }
]

// Rank + label tables live INLINE inside each exported function (FA204/frank
// discipline — see personaDisabledProps below): rank mirrors the server's
// WORKSPACE_ROLE_RANK minus owner ({ admin: 4, editor: 3, viewer: 2,
// guest: 1 }) + the legacy 'member' alias at viewer rank; labels cover the
// FULL enum (a session can carry `editor` even while the picker offers 3
// rows) + the same legacy alias. Keep every inline copy in sync by hand.
export function personaRoleLabel(role) {
  const labels = {
    admin: 'Admin',
    editor: 'Manager',
    viewer: 'Team member',
    member: 'Team member',
    guest: 'Guest'
  }
  return labels[role] || 'a different persona'
}

// Normalize a `_personaSession` state value to its ROLE string (or null).
// Two shapes are accepted:
//   * the SERVER-TRUTH shape — `{ role, sid, startedAt, … }` — what the
//     worker's scope block, GET /persona (the reload oracle) and
//     personaSession.js's `personaStateFromSession` all emit;
//   * the legacy pre-cutover sketch — `{ type: 'role', id }` — tolerated so
//     an older writer can never blank the pill's label mid-migration.
// Anything else (including the forbidden `{ type: 'user' }`) → null.
export function personaSessionRole(session) {
  if (!session || typeof session !== 'object') return null
  if (typeof session.role === 'string' && session.role) return session.role
  if (session.type === 'role' && typeof session.id === 'string' && session.id) return session.id
  return null
}

// Any TRUTHY `_personaSession` counts as "active", regardless of shape —
// deliberately permissive. This feeds the EXIT pill, which is a security
// control (tickets/fable.md PERSONA-6 §5): if something upstream ever writes
// a malformed session, the exit affordance must still appear rather than
// fail closed and strand the admin in a simulated view with no way out.
export function personaActive(root) {
  return !!(root && root._personaSession)
}

// Attribution helper for tickets/fable.md PERSONA-6 decision #3: a control
// the active persona role cannot use gets a REAL `disabled` + a reason,
// never just heavier styling. Returns `{ disabled, title }` — spread
// `disabled` directly onto the control, wire `title` as the native tooltip
// (or into a richer Tooltip if the call site already has one).
//
// ⚠ Never pair the returned `disabled` with `opacity` to dim the control —
// DESIGN_LANGUAGE §8.5 rule 6 ("never dim with opacity on top of an
// already-muted token") and the shipped fix in workspace `dd83fc31` (an
// `opacity: .5` fake-disable on activity-scoring's SaveBtn blended a ~19:1
// pair down to 3.87:1, a contrast failure). Consumers should style
// `:disabled` with a real token-driven fill/ink step instead, e.g.
// `':disabled': { background: 'overlay', color: 'caption', cursor: 'not-allowed' }`
// — the exact pattern `dd83fc31` landed.
//
// Fails CLOSED (disables) on an unrecognized role or session shape — the
// OPPOSITE safe-default from `personaActive` above, deliberately: this
// function is about hiding an ACTION, where "don't allow it" is the safe
// unknown-case default; `personaActive` is about hiding the EXIT, where
// "always show it" is the safe unknown-case default. Same file, two
// intentionally different fail directions — see the tests for both.
export function personaDisabledProps(root, minRole) {
  const session = root && root._personaSession
  if (!session) return { disabled: false, title: null }
  // Self-contained copies — FA204/frank trap: frank.toJSON serializes each
  // functions-file export independently, so a bare reference to a
  // module-local binding (PERSONA_ROLE_RANK) or a sibling exported function
  // (personaRoleLabel) reads as undefined once this file is served to a
  // consuming app (project_frank_drops_module_local_helpers — same class as
  // every other fix in this sweep; workspaceModuleRef.js's
  // applyCollectionsSync duplicates its own `defaultOps` literal rather than
  // reading the exported WS_APP_RECORD_OPS bare for the identical reason).
  // Keep both literals in sync with PERSONA_ROLE_RANK/PERSONA_ROLE_CATALOG
  // above by hand.
  const roleRank = { admin: 4, editor: 3, viewer: 2, member: 2, guest: 1 }
  const roleLabel = (role) =>
    ({
      admin: 'Admin',
      editor: 'Manager',
      viewer: 'Team member',
      member: 'Team member',
      guest: 'Guest'
    })[role] || 'a different persona'
  // Both session shapes (inline copy of personaSessionRole — same FA204
  // discipline): server-truth `{ role }` first, legacy `{ type:'role', id }`
  // tolerated; anything else (incl. the forbidden `{ type:'user' }`) → null
  // → rank 0 → fail CLOSED.
  const sessionRole =
    typeof session.role === 'string' && session.role
      ? session.role
      : session.type === 'role' && typeof session.id === 'string' && session.id
        ? session.id
        : null
  const activeRank = sessionRole ? (roleRank[sessionRole] ?? 0) : 0
  const minRank = roleRank[minRole] ?? 0
  if (activeRank >= minRank) return { disabled: false, title: null }
  const label = sessionRole ? roleLabel(sessionRole) : 'this persona'
  return { disabled: true, title: `Not available to ${label}` }
}

// Thin, DEFENSIVE wrappers around the SDK persona surface (tickets/
// sonnet.md PERSONA-4 — SHIPPED, sdk 26402c58: sdk.startPersona/
// endPersona/getPersona). Registered functions (Rule 8) — call via
// `el.call('startPersonaRole', role)` / `el.call('endPersonaSession')`,
// `this` is the element (same convention as `installAppShellSync` in
// components/AppShell.js). Both still no-op with a console.warn instead of
// throwing when the SDK method is missing — that now guards published-
// version skew (an older @symbo.ls/sdk in a deployed bundle), not an
// unbuilt feature, so a click can never break on a stale dependency.
// PERSONA-5 fix (was the missing half of "wire persona into shell state"):
// this wrapper called `sdk.startPersona` and returned a bare boolean without
// EVER writing `_personaSession` — the picker's `onClick` (components/
// PersonaPill.js `PersonaPicker.childProps.onClick`) has no follow-up write
// either, so picking a role narrowed the admin's SERVER-side permissions
// (the token adopted into TokenManager really did carry the persona claim)
// while the CLIENT stayed blind: no "Viewing as <role>" banner, and — the
// HARD INVARIANT's own words — no exit affordance, because `personaActive`
// reads `root._personaSession`, which nothing had ever set. A persona with
// fewer permissions silently hiding its own exit is exactly the trap the
// invariant forbids; it just arrived via a state write nobody made instead
// of a z-index fight. Fixed here: on a successful start, normalize the
// server's returned claim into the SAME `{ role, sid, startedAt,
// actorWorkspaceRole }` shape `refreshPersonaSession`/`personaStateFromSession`
// use (single shape, one writer path either way) and write it — the pill
// reacts immediately, no reload/oracle round trip required. `role` falls
// back to the requested value if the server response is thin, so the label
// is never blank even on an unexpected envelope.
export async function startPersonaRole(role) {
  const el = this
  if (!el || !el.sdk || typeof el.sdk.startPersona !== 'function') {
    console.warn('[persona] sdk.startPersona is not available (pre-PERSONA-4 @symbo.ls/sdk)')
    return false
  }
  try {
    // Role-only shape — the shipped contract (server 886a9b27 + sdk
    // 26402c58) made the draft's `{ type, id }` axis structurally
    // unrepresentable (its only other value was the forbidden per-person
    // one), so the SDK takes `{ role }` and refuses anything user-shaped.
    // NB `role` must be a REAL workspace tier (sdk PERSONA_TARGET_ROLES:
    // admin/editor/viewer/guest — owner excluded). The picker owns mapping
    // its UI labels ("[team member]") onto that enum — PERSONA-6 territory;
    // the catalog above is labels, not wire values.
    const data = await el.sdk.startPersona({ role })
    const persona = data && typeof data === 'object' ? data.persona : null
    // PER-TAB PERSISTENCE (Nika, 2026-08-14: persona isolation is per-tab).
    // The SDK now installs the token as a sessionStorage OVERLAY instead of
    // overwriting `symbols_access_token`, so the admin's other tabs stay
    // admin. This writes the matching session blob — the same per-tab
    // storage — so a reload of THIS tab keeps showing the lens and its exit
    // affordance, instead of rendering as the admin against persona-narrowed
    // data. The blob deliberately carries no more than the server sent.
    if (persona && data && typeof data.accessToken === 'string') {
      writePersonaSession({
        token: data.accessToken,
        sid: persona.sid,
        role: persona.role || role,
        startedAt: persona.startedAt,
        expiresAt: persona.expiresAt,
        workspaceId: persona.workspaceId,
        actorWorkspaceRole: persona.actorWorkspaceRole
      })
    }
    const state = el.state ? el.state : null
    const root = state && state.root ? state.root : state
    if (root && typeof root.update === 'function') {
      root.update({
        _personaSession: {
          role: (persona && persona.role) || role,
          sid: (persona && persona.sid) || null,
          startedAt: (persona && persona.startedAt) || null,
          actorWorkspaceRole: (persona && persona.actorWorkspaceRole) || null
        }
      })
    }
    return true
  } catch (err) {
    console.warn('[persona] startPersona failed:', (err && err.message) || err)
    return false
  }
}

// The EXIT (tickets/opus.md PERSONA-3 — the escape hatch). One call does the
// whole chain, in trust order:
//   1. SERVER revocation — `sdk.endPersona()` (idempotent, atomic,
//      ownership-bound; POST /core/persona/end → the persona store's guarded
//      single-write; never rejects, resolves { ok, … });
//   2. CLIENT shed — clear the per-tab sessionStorage blob
//      (personaSession.js) so the workspace token provider stops presenting
//      the persona token;
//   3. STATE shed — `_personaSession: null` (+ close the picker) so the pill
//      hides with zero layout shift.
// Steps 2–3 run ONLY when the end is honest (server acknowledged, or there
// was nothing to end). On ok:false the persona is genuinely still in force
// (unreachable server AND unrefreshable token) — everything is left in
// place so the banner keeps telling the truth and the exit stays visible.
// Even without sdk.endPersona (published-version skew) the LOCAL shed still
// runs: the admin's way out can never depend on a dependency version — the
// discarded token's server row only ever NARROWS and expires on its own; a
// later end/sweep (any tab, base token, empty body) cleans it up.
export async function endPersonaSession() {
  const el = this
  const state = el && el.state ? el.state : null
  const root = state && state.root ? state.root : state
  const shedLocal = () => {
    clearPersonaSession()
    if (root && typeof root.update === 'function') {
      root.update({ _personaSession: null, _personaPickerOpen: false })
    }
  }
  if (!el || !el.sdk || typeof el.sdk.endPersona !== 'function') {
    console.warn(
      '[persona] sdk.endPersona is not available (pre-PERSONA-4 @symbo.ls/sdk) — shedding the persona locally; the server row expires on its own'
    )
    shedLocal()
    return true
  }
  try {
    // sdk.endPersona NEVER rejects — it resolves { ok, wasActive, ... }
    // and reports ok:false only when the persona is genuinely still in
    // force (unreachable server AND unrefreshable token), so the exit
    // affordance can stay visible instead of lying. Surface that as this
    // wrapper's boolean.
    const result = await el.sdk.endPersona()
    const ok = !result || result.ok !== false
    if (ok) shedLocal()
    return ok
  } catch (err) {
    console.warn('[persona] endPersona failed:', (err && err.message) || err)
    return false
  }
}

// The RELOAD-TRUTH reconciler (tickets/opus.md PERSONA-3: "reload must
// re-render the pill from server truth, not client state"). Call
// `el.call('refreshPersonaSession')` once at pill mount (and after an exit):
// it asks the SERVER — `sdk.getPersonaSession()`, the worker's
// persona-LENIENT GET /persona oracle, which classifies the presented
// token's claim through `resolvePersonaScope` and answers 200 from ANY
// persona state — and makes `_personaSession` agree with the answer:
//   * { active: true, role, … }   → (re)write state from the server block —
//     a reload restores AS persona even if client state was lost, and a
//     spoofed/stale client value is overwritten by truth;
//   * { active: false, stale: true, reason } → the token's session is DEAD
//     (ended remotely, expired, demoted actor): shed the blob + state so the
//     tab recovers to the honest base session instead of trapping the admin
//     behind a banner the server no longer honors;
//   * { active: false }           → the transport presented no persona claim:
//     any leftover client persona state is a lie (nothing is narrowed) —
//     cleared. The oracle rides the SAME token pipeline as every data
//     request, so its answer describes exactly the session the data routes
//     are living in.
// Fail-SAFE on transport error (offline, 5xx): state is left untouched and
// `false` returned — never drop a live lens on a maybe, never fabricate one.
// ONE request per boot (2026-10-03): loadPrivateData asks at priv:start with
// `{ boot: true }` (the data wave), so the pill's own mount call
// (`{ mount: true }`, a stage-CHROME subtree that used to be the only caller -
// GET /persona went out at 3253 ms on dev 1061f366e, after the data wave) is a
// no-op once a boot read started. Concurrent calls share the in-flight
// request. A call with no flag (e.g. after a persona start) always asks the
// server. The holder is a global, not a module-scope binding (FA204).
export async function refreshPersonaSession(opts) {
  const el = this
  const mode = opts && typeof opts === 'object' ? opts : {}
  const holder = (globalThis.__personaRefresh = globalThis.__personaRefresh || {
    pending: null,
    bootStarted: false
  })
  if (mode.mount && holder.bootStarted) return holder.pending || true
  if (holder.pending) return holder.pending
  if (mode.boot) holder.bootStarted = true
  holder.pending = (async () => {
    if (!el || !el.sdk || typeof el.sdk.getPersonaSession !== 'function') {
      // Pre-oracle @symbo.ls/sdk — nothing to reconcile against; the pill
      // keeps rendering from local state (fail open toward showing the exit).
      return false
    }
    const state = el.state ? el.state : null
    const root = state && state.root ? state.root : state
    if (!root || typeof root.update !== 'function') return false
    try {
      const res = await el.sdk.getPersonaSession()
      const truth = res && typeof res === 'object' && 'active' in res ? res : res && res.data
      if (!truth || typeof truth !== 'object') return false
      if (truth.active === true && typeof truth.role === 'string') {
        root.update({
          _personaSession: {
            role: truth.role,
            sid: truth.sid || null,
            startedAt: truth.startedAt || null,
            actorWorkspaceRole: truth.actorWorkspaceRole || null
          }
        })
        return true
      }
      if (truth.active === false) {
        if (truth.stale) {
          console.warn(
            '[persona] server no longer honors this persona session (' +
              (truth.reason || 'unknown') +
              ') — recovering to the base session'
          )
          clearPersonaSession()
        }
        if (root._personaSession) {
          root.update({ _personaSession: null, _personaPickerOpen: false })
        }
        return true
      }
      return false
    } catch (err) {
      console.warn(
        '[persona] getPersonaSession failed — keeping current persona state:',
        (err && err.message) || err
      )
      return false
    }

  })().finally(() => {
    holder.pending = null
  })
  return holder.pending
}
